import { Suspense, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import HistoryIcon from "@mui/icons-material/History";
import {
  Alert,
  Button,
  Chip,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Navigate,
  Link as RouterLink,
  useNavigate,
  useParams,
} from "react-router-dom";
import { match } from "ts-pattern";
import type { Promisable } from "type-fest";

import type {
  Assessment,
  AssessmentId,
  CurrentUser,
  DeleteAssessmentRequest,
  EmailAddress,
  InvolveGuestAsAssessorRequest,
  InvolveGuestAsSharedAssessmentReaderRequest,
  MergedPersonRecord,
  Person,
  PersonId,
  PersonInvolvement,
  PersonInvolvementId,
  UpdatePersonRequest,
} from "@thalamos/haven-common";
import {
  displayName,
  MatchEarlierGuestsRequestSchema,
  PersonSearchRequestSchema,
  personStateOf,
  StartAssessmentRequestSchema,
  StartFormDraftRequestSchema,
} from "@thalamos/haven-common";

import type { ProgressReading } from "../../api/formDraft/readAssessmentProgress.js";
import { readAssessmentProgress } from "../../api/formDraft/readAssessmentProgress.js";
import api, { apiHooks } from "../../api/index.js";
import { ActivityLogDialog } from "../../components/ActivityLogDialog/ActivityLogDialog.js";
import {
  ArchivePersonDialog,
  type ArchivalOutcome,
} from "../../components/ArchivePersonDialog/ArchivePersonDialog.js";
import type {
  AskOutcome,
  EarlierGuestsMatch,
} from "../../components/AskGuestDialog/AskGuestDialog.js";
import type {
  GuestSharing,
  HistoryPaging,
} from "../../components/AssessmentHistory/AssessmentHistory.js";
import type { FinishedAssessmentRow } from "../../components/AssessmentHistory/finished_assessment_row.js";
import type { CaseInvolvementActor } from "../../components/CaseInvolvement/CaseInvolvement.js";
import type {
  AssessmentStarting,
  CurrentAssessmentRow,
  GuestAsking,
  GuestEnding,
} from "../../components/CurrentAssessments/CurrentAssessments.js";
import type { DeletionOutcome } from "../../components/DeleteAssessmentDialog/DeleteAssessmentDialog.js";
import type { GuestAccessEndingOutcome } from "../../components/EndGuestAccessDialog/EndGuestAccessDialog.js";
import { AllocateResponsibleUserDialog } from "../../components/InvolvementDialogs/InvolvementDialogs.js";
import {
  bannerName,
  PersonBanner,
} from "../../components/PersonBanner/PersonBanner.js";
import type { PersonSaveOutcome } from "../../components/PersonForm/PersonForm.js";
import type { ShareWithGuestOutcome } from "../../components/ShareWithGuestDialog/ShareWithGuestDialog.js";
import type { StartOutcome } from "../../components/StartAssessmentDialog/StartAssessmentDialog.js";
import { useUser } from "../../Context/UserContext.js";
import { QuickSubmit } from "../../devtools/quickSubmit/index.js";
import { createRoute } from "../../routing/createRoute.js";
import { useDirectoryQuery } from "../Admin/useDirectoryQuery.js";
import { assessmentHistoryRows } from "./assessmentHistory.js";
import { AssessmentsTab } from "./AssessmentsTab.js";
import { previousForStart, useCarryOverOffer } from "./carryOverOffer.js";
import {
  CaseInvolvementTab,
  type CaseGuestsSection,
} from "./CaseInvolvementTab.js";
import { currentAssessmentRows } from "./currentAssessments.js";
import { IdentityTab, type PersonMerging } from "./IdentityTab.js";
import type { InvolvementManagement } from "./involvementManagement.js";
import {
  assessmentActions,
  canArchive,
  canReadLog,
  caseInvolvementActor,
  definitiveStart,
  guestDetailsOffered,
  involvementManagement,
  responsibleUserAllocation,
  type ResponsibleUserAllocation,
} from "./involvementManagement.js";
import {
  openingTab,
  PERSON_TABS,
  tabOf,
  type PersonTab,
} from "./personTabs.js";
import { usePerson, type PersonLoadFailure } from "./usePerson.js";
import { writeSucceeded } from "./writeSucceeded.js";

/**
 * Whether the session may merge, read off the kind of work it is acting in.
 *
 * Merging is administrative work on records rather than case work, which is why
 * it follows the records administrator and not the person running the case. It
 * decides what to render and never what is permitted: the server refuses a
 * merge from any other role, and this only spares the others a button that
 * would always be refused.
 */
const canMerge = (user: CurrentUser | undefined): boolean =>
  user?.sessionContext?.kind === "organisation_administration";

const CALENDAR_DATE = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" });

const TAB_LABEL_KEYS: Record<PersonTab, string> = {
  assessments: "personPage.tabs.assessments",
  identity: "personPage.tabs.identity",
  "case-involvement": "personPage.tabs.caseInvolvement",
};

const tabId = (tab: PersonTab) => `person-tab-${tab}`;

/** Archiving and unarchiving a profile, where this session may do either. */
export type PersonArchiving = {
  onArchive: () => Promise<ArchivalOutcome>;
  onUnarchive: () => Promise<"unarchived" | "failed">;
};

/** One young person's record, and everything merged into it. */
export const PersonPage = () => {
  const { personId, tab: segment } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { t } = useTranslation();

  // `useParams` types every segment as optional, but the route cannot match
  // without this one. The fallback satisfies the type rather than describing a
  // state the page can be in.
  const requestedId = personId ?? "";
  const tab = tabOf(segment);
  const record = usePerson(requestedId, {
    history: tab === "assessments",
    guests: tab === "case-involvement",
  });
  const { person, involvements, refetch } = record;

  const [
    {
      data: configuration,
      error: configurationError,
      response: configurationResponse,
    },
  ] = apiHooks.configuration.effective();

  const candidateSearch = useDirectoryQuery();
  const [{ data: candidates, loading: candidatesLoading }] =
    apiHooks.person.search(
      PersonSearchRequestSchema.parse({
        query: candidateSearch.query,
        limit: 10,
      }),
    );

  const save = async (
    id: PersonId,
    request: UpdatePersonRequest,
  ): Promise<PersonSaveOutcome> => {
    // Re-read rather than trusting what was sent, so the page shows the record
    // as the server settled it.
    const succeeded = await writeSucceeded(
      api.person.replace(id, request),
      200,
      refetch,
    );

    return succeeded ? "saved" : "failed";
  };

  const merging: PersonMerging | undefined = canMerge(user)
    ? {
        onMerge: async ({ targetPersonId, request }) =>
          (await writeSucceeded(
            api.person.merge(targetPersonId, request),
            201,
            refetch,
          ))
            ? "merged"
            : "failed",
        onUndo: async (unmerge) =>
          (await writeSucceeded(api.person.unmerge(unmerge), 200, refetch))
            ? "merged"
            : "failed",
        candidateSearch: {
          value: candidateSearch.input,
          onChange: candidateSearch.setQuery,
          results: candidates?.results ?? [],
          loading: candidatesLoading,
        },
      }
    : undefined;

  const readGuestDetails = async (involvementId: PersonInvolvementId) =>
    person === undefined
      ? undefined
      : api.personGuest.readDetails(person.id, involvementId);

  const deleteAssessment = async ({
    assessmentId,
    request,
  }: {
    assessmentId: AssessmentId;
    request: DeleteAssessmentRequest;
  }): Promise<DeletionOutcome> => {
    // A deleted assessment is no longer listed; re-read rather than wait on a
    // realtime event a deployment may not deliver.
    const succeeded = await writeSucceeded(
      api.assessment.delete(assessmentId, request),
      204,
      record.refetchAssessments,
    );

    return succeeded ? "deleted" : "failed";
  };

  const archiving: PersonArchiving | undefined =
    person === undefined || !canArchive(user, involvements, person)
      ? undefined
      : {
          onArchive: async () =>
            (await writeSucceeded(api.person.archive(person.id), 204, refetch))
              ? "archived"
              : "failed",
          onUnarchive: async () =>
            (await writeSucceeded(
              api.person.unarchive(person.id),
              204,
              refetch,
            ))
              ? "unarchived"
              : "failed",
        };

  const offered = assessmentActions(user, involvements, person);
  const carryOverOffer = useCarryOverOffer(person?.id, offered.start);

  // Opened in the form once started, so the assessor lands on its questions
  // rather than back on the record.
  const start = async (
    instrument: string,
    carryOver: boolean,
  ): Promise<StartOutcome> => {
    const request = StartAssessmentRequestSchema.safeParse({ instrument });

    // Unreachable: the start dialog renders only once the person is read, and
    // offers instruments from configuration parsed as an enum of versions. Kept
    // so a drift fails politely.
    /* v8 ignore if */
    if (person === undefined || !request.success) {
      return "failed";
    }

    const started = await api.assessment.start(person.id, request.data);

    if (started.status !== 201) {
      return "failed";
    }

    const { id } = started.data as Assessment;

    // Created before navigating, so the choice survives the tab closing before
    // the form opens. Whatever stops it is not reported here: the form, told
    // the choice, asks again as it opens and says so if it is refused.
    if (carryOver) {
      await api.formDraft
        .create(id, StartFormDraftRequestSchema.parse({ carryOver: true }))
        .catch(() => undefined);
    }

    navigate(
      createRoute.assessment(id),
      carryOver ? { state: { carryOver: true } } : undefined,
    );
    return "started";
  };
  const definitive = definitiveStart(user, involvements, record.assessmentList);

  const askGuest = async (
    request: InvolveGuestAsAssessorRequest,
  ): Promise<AskOutcome> => {
    // Unreachable: the dialog renders only once the person is read.
    /* v8 ignore if */
    if (person === undefined) {
      return { kind: "failed" };
    }

    const asked = await api.personGuest
      .involveGuestAsAssessor(person.id, request)
      .catch(() => undefined);

    return (
      match(asked?.status)
        .returnType<Promise<AskOutcome>>()
        .with(201, async () => {
          // The guest's involvement and assessment are new rows on this page;
          // re-read rather than wait on a realtime event a deployment may not
          // deliver.
          await Promise.all([
            record.refetchInvolvements(),
            record.refetchAssessments(),
          ]).catch(() => undefined);
          return { kind: "asked" };
        })
        .with(400, () =>
          Promise.resolve({
            kind: "address_refused",
            message: asked?.error?.message ?? t("askGuest.outcome.failed"),
          }),
        )
        // A guest signs in with the person's date of birth, so a record
        // without one is refused as a conflict too.
        .with(409, () =>
          Promise.resolve(
            person.dateOfBirth === null
              ? { kind: "no_date_of_birth" }
              : { kind: "already_asked" },
          ),
        )
        .otherwise(() => Promise.resolve({ kind: "failed" }))
    );
  };

  const shareWithGuest = async (
    request: InvolveGuestAsSharedAssessmentReaderRequest,
  ): Promise<ShareWithGuestOutcome> => {
    // Unreachable: the dialog renders only once the person is read.
    /* v8 ignore if */
    if (person === undefined) {
      return { kind: "failed" };
    }

    const shared = await api.personGuest
      .involveGuestAsSharedAssessmentReader(person.id, request)
      .catch(() => undefined);

    return (
      match(shared?.status)
        .returnType<Promise<ShareWithGuestOutcome>>()
        .with(201, async () => {
          // The guest's involvement and the history's counts change on this
          // page; re-read rather than wait on a realtime event a deployment
          // may not deliver.
          await Promise.all([
            record.refetchInvolvements(),
            record.refetchAssessments(),
          ]).catch(() => undefined);
          return { kind: "shared" };
        })
        .with(400, () =>
          Promise.resolve({
            kind: "address_refused",
            message:
              shared?.error?.message ?? t("shareWithGuest.outcome.failed"),
          }),
        )
        // A guest signs in with the person's date of birth, so a record
        // without one is refused as a conflict too.
        .with(409, () =>
          Promise.resolve(
            person.dateOfBirth === null
              ? { kind: "no_date_of_birth" }
              : { kind: "already_shared" },
          ),
        )
        .otherwise(() => Promise.resolve({ kind: "failed" }))
    );
  };

  const endGuestAccess = async (
    involvementId: PersonInvolvementId,
  ): Promise<GuestAccessEndingOutcome> => {
    // Unreachable: the dialog renders only once the person is read.
    /* v8 ignore if */
    if (person === undefined) {
      return "failed";
    }

    const ended = await api.personGuest
      .endGuestAccess(person.id, involvementId)
      .catch(() => undefined);

    // Re-read on a refusal too, rather than wait on a realtime event a
    // deployment may not deliver: a conflict means the guest has already
    // ended or sent, and the row offering End is out of date either way.
    await Promise.all([
      record.refetchInvolvements(),
      record.refetchAssessments(),
      record.refetchGuests(),
    ]).catch(() => undefined);

    return ended?.status === 204 ? "ended" : "failed";
  };

  const matchEarlierGuests = async (
    email: EmailAddress,
  ): Promise<EarlierGuestsMatch> => {
    // Unreachable: the dialog renders only once the person is read.
    /* v8 ignore if */
    if (person === undefined) {
      return { kind: "failed" };
    }

    const matched = await api.personGuest
      .matchEarlierGuests(
        person.id,
        MatchEarlierGuestsRequestSchema.parse({ email }),
      )
      .catch(() => undefined);

    return matched === undefined
      ? { kind: "failed" }
      : { kind: "matched", earlierGuests: matched.earlierGuests };
  };

  const followedMergedRecord = person?.mergedPersonRecords.find(
    (merged) => merged.id === requestedId,
  );
  // The record the address names, which may be one merged into `person`, so a
  // change or a link names the record the page was asked for.
  const namedRecordId =
    person === undefined ? undefined : (followedMergedRecord?.id ?? person.id);

  const managing =
    namedRecordId === undefined
      ? undefined
      : {
          personId: namedRecordId,
          user,
          involvements,
          person,
          reread: record.refetchInvolvements,
          // Passed straight through: naming the calls here rather than handing
          // over the whole client is what keeps `involvementManagement`
          // testable without one.
          api: {
            findUsers: api.user.find,
            findOrganisations: api.organisation.find,
            start: api.involvement.start,
            end: api.involvement.end,
            allocateResponsibleUser: api.involvement.allocateResponsibleUser,
            replaceResponsibleUser: api.involvement.replaceResponsibleUser,
          },
          leave: () => {
            navigate(createRoute.persons());
          },
        };

  // An address naming no tab opens where the profile needs attention, which
  // waits on the involvements: before they arrive, every profile would look
  // unassigned.
  const choosingTab = tab === undefined && namedRecordId !== undefined;

  if (choosingTab && record.involvementsSettled) {
    return (
      <Navigate
        replace
        to={createRoute.personTab(namedRecordId, openingTab(record.state))}
      />
    );
  }

  return (
    <>
      <PersonPageContent
        followedMergedRecord={followedMergedRecord}
        tab={tab ?? "assessments"}
        onShowAllDetails={
          tab === "identity" || namedRecordId === undefined
            ? undefined
            : () => {
                navigate(createRoute.personTab(namedRecordId, "identity"));
              }
        }
        person={person}
        // The first read only: a re-read, as a live update makes, keeps the
        // page and any dialog open on it in place.
        loading={(record.loading && person === undefined) || choosingTab}
        failure={record.failure}
        canReadLog={canReadLog(user, involvements, person)}
        involvements={involvements}
        involvementsFailed={record.involvementsFailed}
        management={
          managing === undefined ? undefined : involvementManagement(managing)
        }
        allocation={
          managing === undefined
            ? undefined
            : responsibleUserAllocation(managing)
        }
        archiving={archiving}
        merging={merging}
        involvementActor={caseInvolvementActor(user, involvements, person)}
        assessmentRows={
          record.assessmentList === undefined
            ? undefined
            : currentAssessmentRows(
                record.assessmentList,
                user,
                involvements,
                person,
              )
        }
        historyRows={
          // Whether each may be read depends on who is involved, so it waits
          // for them rather than calling every one withheld in the meantime.
          record.assessmentHistory.page === undefined ||
          involvements === undefined
            ? undefined
            : assessmentHistoryRows(
                record.assessmentHistory.page.results,
                user,
                involvements,
                person,
                record.assessmentHistory.page.guestsSharedWith,
              )
        }
        historyPaging={
          record.assessmentHistory.page === undefined
            ? undefined
            : {
                total: record.assessmentHistory.page.total,
                loading: record.assessmentHistory.loading,
                failed: record.assessmentHistory.rereadFailed,
                onShowMore: record.assessmentHistory.onShowMore,
              }
        }
        historyFailed={record.assessmentHistory.failed}
        assessmentListFailed={record.assessmentListFailed}
        readAssessmentProgress={readAssessmentProgress}
        onDeleteAssessment={deleteAssessment}
        assessmentStarting={
          offered.start
            ? {
                personName:
                  (person === undefined
                    ? undefined
                    : bannerName(person.names)) ??
                  t("personPage.banner.noCurrentName"),
                definitive:
                  definitive === undefined
                    ? undefined
                    : { approverName: definitive.approverName },
                definitiveUnderWay: definitive?.definitiveUnderWay ?? false,
                // Held back until what a start would follow is known too, so
                // the dialog does not change its mind once it is showing.
                versions:
                  configurationResponse?.status === 200 &&
                  carryOverOffer.settled
                    ? configuration?.availableInstrumentVersions
                    : undefined,
                previous:
                  carryOverOffer.settled && carryOverOffer.source !== undefined
                    ? previousForStart(carryOverOffer.source, user?.id)
                    : undefined,
                versionsFailed:
                  (configurationError !== undefined &&
                    configurationError !== null) ||
                  (configurationResponse !== undefined &&
                    configurationResponse.status !== 200),
                onStart: start,
              }
            : undefined
        }
        guestAsking={
          offered.askGuest
            ? {
                versions:
                  configurationResponse?.status === 200
                    ? configuration?.availableInstrumentVersions
                    : undefined,
                versionsFailed:
                  (configurationError !== undefined &&
                    configurationError !== null) ||
                  (configurationResponse !== undefined &&
                    configurationResponse.status !== 200),
                onMatch: matchEarlierGuests,
                onAsk: askGuest,
              }
            : undefined
        }
        guestEnding={
          offered.endGuestAccess ? { onEnd: endGuestAccess } : undefined
        }
        guests={{
          guests: record.guests,
          failed: record.guestsFailed,
          mayOpenDetails: guestDetailsOffered(user, involvements, person),
          readDetails: readGuestDetails,
          onEnd: offered.endGuestAccess ? endGuestAccess : undefined,
          assessmentHref: createRoute.assessment,
        }}
        guestSharing={
          offered.askGuest && person !== undefined
            ? {
                personName:
                  bannerName(person.names) ??
                  t("personPage.banner.noCurrentName"),
                onMatch: matchEarlierGuests,
                onShare: shareWithGuest,
              }
            : undefined
        }
        onSave={save}
      />
      {QuickSubmit === undefined || person === undefined ? null : (
        <Suspense fallback={null}>
          <QuickSubmit
            personId={person.id}
            onAssessmentMade={() => {
              void record.refetchAssessments();
            }}
          />
        </Suspense>
      )}
    </>
  );
};

/**
 * The page's rendering, with the record and every action injected so a story or
 * a test can show any state it has without an API client.
 *
 * Loading is told apart from both failures: someone who reads "no such record"
 * while the request is still in flight has been told something untrue.
 */
export const PersonPageContent = ({
  followedMergedRecord,
  tab,
  onShowAllDetails,
  person,
  loading,
  failure,
  canReadLog: mayReadLog,
  involvements,
  involvementsFailed,
  management,
  allocation,
  archiving,
  merging,
  involvementActor,
  assessmentRows,
  historyRows,
  historyPaging,
  historyFailed,
  assessmentListFailed,
  assessmentStarting,
  guestAsking,
  guestEnding,
  guestSharing,
  guests,
  readAssessmentProgress,
  onDeleteAssessment,
  onSave,
}: {
  /** The record merged into `person` that the address names, if it names one. */
  followedMergedRecord?: MergedPersonRecord | undefined;
  tab: PersonTab;
  /** Opens the tab holding everything the record says about the young person. */
  onShowAllDetails?: () => void;
  person: Person | undefined;
  loading: boolean;
  failure: PersonLoadFailure | undefined;
  /** Whether this session may read what has been done to the record; see `canReadLog`. */
  canReadLog: boolean;
  /**
   * Everybody involved with this record. Injected like the record itself, so a
   * story can show every involvement without standing up the API.
   */
  involvements?: PersonInvolvement[];
  involvementsFailed?: boolean;
  /** Absent for a reader who may not change who is involved. */
  management?: InvolvementManagement | undefined;
  /** Absent unless this reader may name who runs a case nobody runs. */
  allocation?: ResponsibleUserAllocation | undefined;
  /** Absent unless this reader may archive and unarchive the profile. */
  archiving?: PersonArchiving | undefined;
  /** Absent unless this reader may merge records into this one. */
  merging?: PersonMerging | undefined;
  /** Who this reader acts as on Case involvement; absent for a reader. */
  involvementActor?: CaseInvolvementActor | undefined;
  /**
   * The young person's assessments in the workflow, as this session sees them,
   * injected like the record itself; see `currentAssessmentRows`.
   */
  assessmentRows?: CurrentAssessmentRow[] | undefined;
  /**
   * The most recently finished of the young person's assessments, as this
   * session sees them, injected like the record itself; see
   * `assessmentHistoryRows`.
   */
  historyRows?: FinishedAssessmentRow[] | undefined;
  /** Absent where `historyRows` is the whole history. */
  historyPaging?: HistoryPaging | undefined;
  /** Whether the finished assessments could not be read. */
  historyFailed?: boolean;
  assessmentListFailed?: boolean;
  /** Absent where the session may not start an assessment here. */
  assessmentStarting?: AssessmentStarting | undefined;
  /** Absent where the session may not ask a guest to assess here. */
  guestAsking?: GuestAsking | undefined;
  /** Absent where the session may not end a guest's access here. */
  guestEnding?: GuestEnding | undefined;
  /** Absent where the session may not share assessments with a guest here. */
  guestSharing?: GuestSharing | undefined;
  /** The guests on the case, for Case involvement; absent shows no Guests section. */
  guests?: CaseGuestsSection | undefined;
  /** How far an assessment has got, for the dialog that deletes it. */
  readAssessmentProgress: (
    assessmentId: AssessmentId,
  ) => Promisable<ProgressReading>;
  onDeleteAssessment: (deletion: {
    assessmentId: AssessmentId;
    request: DeleteAssessmentRequest;
  }) => Promise<DeletionOutcome>;
  onSave: (
    personId: PersonId,
    request: UpdatePersonRequest,
  ) => Promise<PersonSaveOutcome>;
}) => {
  const { t } = useTranslation();
  const [showingActivity, setShowingActivity] = useState(false);
  const [allocatingResponsibleUser, setAllocatingResponsibleUser] =
    useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const [unarchiving, setUnarchiving] = useState<
    "submitting" | "failed" | undefined
  >(undefined);

  // Unknown until both have loaded: a state worked out from involvements that
  // have not arrived would call every profile unassigned.
  const state =
    person === undefined || involvements === undefined
      ? undefined
      : personStateOf(person, involvements);

  // The record the address names, so the tab links keep naming it.
  const namedRecordId =
    person === undefined ? undefined : (followedMergedRecord?.id ?? person.id);

  return (
    <Stack spacing={3}>
      <Button
        component={RouterLink}
        to={createRoute.persons()}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ alignSelf: "flex-start" }}
      >
        {t("personListing.backToList")}
      </Button>

      {person === undefined ? null : (
        <PersonBanner
          person={person}
          involvements={involvements}
          onShowAllDetails={onShowAllDetails}
        />
      )}

      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        useFlexGap
        flexWrap="wrap"
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* Once the record is here, the banner carries its heading. */}
          {person === undefined ? (
            <Typography variant="h4" component="h1">
              {t("personPage.record")}
            </Typography>
          ) : null}
          {state === undefined ? null : (
            <Chip
              size="small"
              color={state.kind === "active" ? "success" : "default"}
              label={t(`personPage.state.${state.kind}`)}
            />
          )}
        </Stack>

        {person !== undefined &&
        person.archivedAt === null &&
        archiving !== undefined ? (
          <Button
            variant="outlined"
            size="small"
            color="warning"
            onClick={() => {
              setConfirmingArchive(true);
            }}
          >
            {t("personPage.archive.open")}
          </Button>
        ) : null}

        {person !== undefined && mayReadLog ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => {
              setShowingActivity(true);
            }}
          >
            {t("personPage.activity.open")}
          </Button>
        ) : null}
      </Stack>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          {t("common.loading")}
        </Typography>
      ) : person === undefined || namedRecordId === undefined ? (
        <Typography variant="body2" color="error">
          {failure === "not_found"
            ? t("personPage.notFound")
            : t("personPage.loadFailed")}
        </Typography>
      ) : (
        <>
          {followedMergedRecord === undefined ? null : (
            <Alert severity="info">
              {t("personPage.mergedNotice", {
                date: CALENDAR_DATE.format(followedMergedRecord.mergedAt),
              })}
            </Alert>
          )}

          {person.archivedAt === null ? null : (
            <Alert
              severity="warning"
              action={
                archiving === undefined ? undefined : (
                  <Button
                    color="inherit"
                    size="small"
                    disabled={unarchiving === "submitting"}
                    onClick={() => {
                      setUnarchiving("submitting");
                      void archiving
                        .onUnarchive()
                        .catch((): "failed" => "failed")
                        .then((outcome) => {
                          setUnarchiving(
                            outcome === "failed" ? "failed" : undefined,
                          );
                        });
                    }}
                  >
                    {t("personPage.archive.unarchive")}
                  </Button>
                )
              }
            >
              {t("personPage.archive.banner")}
            </Alert>
          )}

          {unarchiving === "failed" ? (
            <Alert severity="error">
              {t("personPage.archive.unarchiveFailed")}
            </Alert>
          ) : null}

          {state?.kind === "unassigned" ? (
            <Alert
              severity="info"
              action={
                allocation === undefined ? undefined : (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setAllocatingResponsibleUser(true);
                    }}
                  >
                    {t("personPage.unassigned.name")}
                  </Button>
                )
              }
            >
              {t("personPage.unassigned.banner", {
                missing: state.missing
                  .map((kind) => t(`access.kind.${kind}`))
                  .join(", "),
              })}
            </Alert>
          ) : null}

          <Tabs
            value={tab}
            aria-label={t("personPage.tabs.label")}
            variant="scrollable"
            allowScrollButtonsMobile
          >
            {PERSON_TABS.map((each) => (
              <Tab
                key={each}
                value={each}
                id={tabId(each)}
                aria-controls="person-tabpanel"
                label={t(TAB_LABEL_KEYS[each])}
                component={RouterLink}
                to={createRoute.personTab(namedRecordId, each)}
              />
            ))}
          </Tabs>

          <Stack
            spacing={3}
            role="tabpanel"
            id="person-tabpanel"
            aria-labelledby={tabId(tab)}
          >
            {match(tab)
              .with("assessments", () => (
                <AssessmentsTab
                  state={state}
                  rows={assessmentRows}
                  history={historyRows}
                  historyPaging={historyPaging}
                  historyFailed={
                    (historyFailed ?? false) || (involvementsFailed ?? false)
                  }
                  failed={assessmentListFailed ?? false}
                  starting={assessmentStarting}
                  guestAsking={guestAsking}
                  guestEnding={guestEnding}
                  guestSharing={guestSharing}
                  caseInvolvementHref={createRoute.personTab(
                    namedRecordId,
                    "case-involvement",
                  )}
                  personName={displayName(person.names)}
                  readProgress={readAssessmentProgress}
                  onDelete={onDeleteAssessment}
                />
              ))
              .with("identity", () => (
                <IdentityTab
                  person={person}
                  merging={merging}
                  onSave={onSave}
                />
              ))
              .with("case-involvement", () => (
                <CaseInvolvementTab
                  personName={displayName(person.names)}
                  state={state}
                  involvements={involvements}
                  involvementsFailed={involvementsFailed ?? false}
                  assessments={assessmentRows?.map((row) => row.assessment)}
                  actor={involvementActor}
                  management={management}
                  onAllocateResponsibleUser={
                    allocation === undefined
                      ? undefined
                      : () => {
                          setAllocatingResponsibleUser(true);
                        }
                  }
                  guests={guests}
                />
              ))
              .exhaustive()}
          </Stack>

          {showingActivity ? (
            <ActivityLogDialog
              personId={person.id}
              involvements={involvements ?? []}
              onClose={() => {
                setShowingActivity(false);
              }}
            />
          ) : null}

          {archiving === undefined ? null : (
            <ArchivePersonDialog
              open={confirmingArchive}
              onClose={() => {
                setConfirmingArchive(false);
              }}
              onArchive={archiving.onArchive}
            />
          )}
          {allocation === undefined ? null : (
            <AllocateResponsibleUserDialog
              open={allocatingResponsibleUser}
              onClose={() => {
                setAllocatingResponsibleUser(false);
              }}
              search={allocation}
              onAllocateResponsibleUser={allocation.onAllocateResponsibleUser}
              onRefresh={allocation.onRefresh}
            />
          )}
        </>
      )}
    </Stack>
  );
};
